/**
 * React Application Entry Point
 */

import "helpers/initFA";
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { PermissionProvider } from "react-permission-role"; // For role-based permissions
import { Provider, useDispatch, useSelector } from "react-redux";
import { RouterProvider } from "react-router-dom"; // Use only RouterProvider

import { logout } from "features/auth/authSlice";
import { selectCurrentLanguage } from "features/language/languageSlice";
import i18n from "i18n";
import AppProvider from "providers/AppProvider";
import { router } from "routes"; // Define routes using createBrowserRouter
import { store } from "store/store";

import {
  getAccessToken,
  isTokenExpired,
  refreshToken,
} from "components/utilities/sedra3/auth";

import paths from "routes/paths";

/**
 * AppWithPermissions Component
 * Handles token validation, permissions, and routing.
 */
const AppWithPermissions = () => {
  const dispatch = useDispatch();

  // Extract roles, permissions and whether the browser is signed in
  const { roles, permissions, isAuthenticated } = useSelector(
    (state) => state.auth
  );

  // Get the current language from Redux
  const currentLanguage = useSelector(selectCurrentLanguage);

  // Define public paths that don't require a token
  const publicPaths = [
    paths.cardLogin,
    paths.cardConfirmMail,
    paths.cardForgotPassword,
    paths.cardResetPassword,
    paths.simpleLogin,
    paths.simpleForgotPassword,
    paths.simpleResetPassword,
    paths.cardTechnicalIssue,
  ];

  // Define public path patterns (for dynamic routes like confirm-email with token)
  const publicPathPatterns = [/^\/authentication\/card\/confirm-email\/[^/]+$/];

  /**
   * Synchronize the initial language from localStorage on component mount.
   */
  useEffect(() => {
    const storedLanguage = localStorage.getItem("language") || "en";
    i18n.changeLanguage(storedLanguage); // Set the language in i18n
  }, []);

  /**
   * Synchronize i18n with Redux language updates.
   */
  useEffect(() => {
    if (currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage]);

  /**
   * Without a session, go to the sign-in page. Without an access token (a
   * reload: it lives in memory only, #112) or with an expired one, renew it
   * (#121).
   */
  useEffect(() => {
    const currentPath = window.location.pathname;

    // Check if current path is in publicPaths or matches any publicPathPatterns
    const isPublicPath =
      publicPaths.includes(currentPath) ||
      publicPathPatterns.some((pattern) => pattern.test(currentPath));

    if (isPublicPath) return;

    if (!isAuthenticated) {
      dispatch(logout()); // Dispatch logout action
      router.navigate(paths.cardLogin); // Redirect to login
      return;
    }

    // A reload or a new tab starts without the access token, and one held in
    // memory lasts two hours of a session of up to twelve: the page renews it
    // through the renewal every request shares, with the refresh token's
    // cookie. A renewal the server refuses signs the user out (auth.js), which
    // brings the browser back here, to the sign-in page; one the portal could
    // not answer keeps the session, and the page's requests say so.
    const token = getAccessToken();
    if (!token || isTokenExpired(token)) {
      refreshToken().catch((error) =>
        console.error("Token refresh failed", error)
      );
    }
  }, [isAuthenticated, dispatch]);

  return (
    <PermissionProvider roles={roles} permissions={permissions}>
      <RouterProvider router={router} />
    </PermissionProvider>
  );
};

/**
 * Mount the application to the DOM
 */
const container = document.getElementById("main");
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <AppProvider>
        <I18nextProvider i18n={i18n}>
          <AppWithPermissions />
        </I18nextProvider>
      </AppProvider>
    </Provider>
  </React.StrictMode>
);
